import { useCallback, useMemo, useRef, useState } from 'react';

import {
  useGetOrCreateAiUserChatQuery,
  useGetUserChatMessagesQuery,
  useSendUserChatMessageMutation,
  useUserChatMarkAsReadMutation,
  useUserChatResetContextMutation,
} from '@app/api/UserChat';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useErrorHandler } from '@app/shared/hooks';
import { clog } from '@app/utils/clog';
import {
  createWSConnection,
  invokeWSMethod,
  subscribeToWsEvent,
  unsubscribeFromWsEvent,
} from '@app/utils/createWSConnection';

import type {
  ChatMessageNewPayload,
  UseAiAgentsResult,
} from '@pages/AiAgents/types';
import { mapMessagesToViewModel } from '@pages/AiAgents/utils/mapMessages';

export const useAiAgents = (): UseAiAgentsResult => {
  const {
    params: { ActiveBranchId, UserId, Database },
  } = useUserPrint();

  const { showNotification } = useNotification();

  const hasActiveBranchId = ActiveBranchId != null;

  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  const [inputValue, setInputValue] = useState('');

  const {
    data: aiChat,
    isLoading: isChatLoading,
    isError: isChatError,
    error: chatError,
  } = useGetOrCreateAiUserChatQuery(
    { ActiveBranchId: ActiveBranchId! },
    {
      skip: !hasActiveBranchId,
    }
  );

  const chatId = useMemo(
    () => (aiChat?.ChatId != null ? aiChat.ChatId : null),
    [aiChat]
  );

  const shouldSkipMessagesQuery = !hasActiveBranchId || chatId == null;

  const {
    data: messagesData,
    isLoading: isMessagesLoading,
    isError: isMessagesError,
    error: messagesError,
    refetch: refetchMessages,
  } = useGetUserChatMessagesQuery(
    {
      ActiveBranchId: ActiveBranchId!,
      chatId: chatId ?? 0,
      limit: 50,
    },
    { skip: shouldSkipMessagesQuery }
  );

  useErrorHandler([
    { flag: isChatError, error: chatError },
    { flag: isMessagesError, error: messagesError },
  ]);

  const [sendMessageMutation, { isLoading: isSending }] =
    useSendUserChatMessageMutation();

  const [markAsReadMutation] = useUserChatMarkAsReadMutation();
  const [chatResetContext, { isLoading: isResetting }] =
    useUserChatResetContextMutation();

  const onReset = useCallback(async () => {
    if (!hasActiveBranchId || chatId == null) {
      return;
    }

    try {
      await chatResetContext({
        ActiveBranchId,
        data: { chatId },
      }).unwrap();

      await refetchMessages();
    } catch (error) {
      showNotification(error);
    }
  }, [
    ActiveBranchId,
    chatId,
    chatResetContext,
    hasActiveBranchId,
    refetchMessages,
    showNotification,
  ]);

  const messages = useMemo(() => {
    const sorted = (messagesData ?? [])
      .slice()
      .sort((left, right) => left.CreatedAt.localeCompare(right.CreatedAt));

    const mapped = mapMessagesToViewModel(sorted, UserId);

    return mapped;
  }, [UserId, , messagesData]);

  const scrollToBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    const skip =
      !chatId || isMessagesLoading || messages.length === 0 || !container;

    if (skip) return;

    container.scrollTop = container.scrollHeight;
  }, [chatId, isMessagesLoading, messages]);

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  const handleSendMessage = useCallback(async () => {
    const trimmed = inputValue.trim();

    if (!trimmed) {
      return;
    }

    if (!hasActiveBranchId || chatId == null) {
      return;
    }

    try {
      await sendMessageMutation({
        ActiveBranchId: ActiveBranchId!,
        data: { ChatId: chatId, MessageText: trimmed },
      }).unwrap();

      setInputValue('');
      await refetchMessages();
      scrollToBottom();

      showNotification('Контекст чата был сброшен.');
    } catch (error) {
      showNotification(error);
    }
  }, [
    ActiveBranchId,
    chatId,
    hasActiveBranchId,
    inputValue,
    refetchMessages,
    sendMessageMutation,
    showNotification,
    scrollToBottom,
  ]);

  const markMessagesAsRead = useCallback(async () => {
    const skip =
      !hasActiveBranchId ||
      chatId == null ||
      !messagesData ||
      messagesData.length === 0;

    if (skip) return;

    const lastMessage = messagesData[messagesData.length - 1];

    try {
      await markAsReadMutation({
        ActiveBranchId: ActiveBranchId!,
        data: { ChatId: chatId, MessageId: lastMessage.Id },
      }).unwrap();
    } catch (error) {
      showNotification(error);
    }
  }, [
    ActiveBranchId,
    chatId,
    hasActiveBranchId,
    markAsReadMutation,
    messagesData,
    showNotification,
  ]);

  const isInputDisabled = useMemo(
    () => chatId == null || isChatLoading || isSending,
    [chatId, isChatLoading, isSending]
  );

  const handleWsChatMessageNew = useCallback(() => {
    void refetchMessages();
  }, [refetchMessages]);

  const handleWsChatMessageRead = useCallback(() => {
    void refetchMessages();
  }, [refetchMessages]);

  const handleWsChatTyping = useCallback(() => {}, []);

  const handleWsChatCreated = useCallback(() => {
    void refetchMessages();
  }, [refetchMessages]);

  const connectWs = useCallback(async () => {
    if (!Database || chatId == null) return;
    try {
      await createWSConnection(
        async () => {
          await invokeWSMethod('ListenChat', Database, chatId);

          subscribeToWsEvent(
            'chat.message.new',
            (data: ChatMessageNewPayload) => {
              clog('UserChat chat.message.new WS event', data);
              handleWsChatMessageNew();
            }
          );

          subscribeToWsEvent(
            'chat.message.read',
            (data: ChatMessageNewPayload) => {
              clog('UserChat chat.message.read WS event', data);
              handleWsChatMessageRead();
            }
          );

          subscribeToWsEvent('chat.typing', (data: ChatMessageNewPayload) => {
            clog('UserChat chat.typing WS event', data);
            handleWsChatTyping();
          });

          subscribeToWsEvent('chat.created', (data: ChatMessageNewPayload) => {
            clog('UserChat chat.created WS event', data);
            handleWsChatCreated();
          });

          clog('UserChat WebSocket connected to AI chat', chatId);
        },
        (error) => {
          showNotification(error);
        }
      );
    } catch (error) {
      showNotification(error);
    }
  }, [
    handleWsChatCreated,
    handleWsChatMessageNew,
    handleWsChatMessageRead,
    handleWsChatTyping,
    showNotification,
    Database,
    chatId,
  ]);

  const disconnectWs = useCallback(async () => {
    unsubscribeFromWsEvent('chat.message.new');
    unsubscribeFromWsEvent('chat.message.read');
    unsubscribeFromWsEvent('chat.typing');
    unsubscribeFromWsEvent('chat.created');
  }, []);

  return {
    messages,
    isChatLoading,
    isMessagesLoading,
    isSending,
    inputValue,
    isInputDisabled,
    handleInputChange,
    handleSendMessage,
    messagesContainerRef,
    scrollToBottom,
    markMessagesAsRead,
    connectWs,
    disconnectWs,
    onReset,
    isResetting,
  };
};
